import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  FormControl,
  Input,
  Modal,
  Typography
} from '@material-ui/core';
import CircularProgress from 'material-ui/CircularProgress';
import AdjustmentReasonWidget from '../AdjustmentReasonWidget/AdjustmentReasonWidget';
import './CustomValueModal.css';
import { DialogueTypes } from './definitions';
import { white } from 'material-ui/styles/colors';
import typography from 'material-ui/styles/typography';
import * as api from '../../../../src/services/api';
import { cloneDeep, debounce } from 'lodash';
import { injectIntl } from 'react-intl';
import {
  formatNumber,
  checkPrecisionLength
} from '../../../utils/format-number-util';
import { PRICE_PRECISION } from '../../../constants/decimal-points';
import { checkFieldValidation } from '../../presenters/renewals/ProductLineItemPage/line-item-page-util';
import { calculateDiscount } from '../../../utils/renewals-util';
import {
  APPROVAL_LEVELS,
  VALIDATION_LEVELS
} from '../../../utils/util-constants';
import { getRegionalColors } from '../../../utils/regional-colors';

const DISCOUNT_PRECISION = 2;

const styles = {
  title: {
    fontSize: 16,
    fontWeight: typography.fontWeightLight,
    backgroundColor: '#003968',
    color: white,
    textAlign: 'center',
    verticalAlign: 'middle'
  }
};

// TODO: This component takes in too many props. It could probably better be implemented utilizing redux.
class CustomValueModal extends PureComponent {
  constructor(props) {
    super(props);

    const defaultAdjustmentReason = !!props.adjustmentReasons
      ? props.adjustmentReasons[0]
      : {
          reasonId: -1,
          reason: ''
        };

    this.state = {
      adjustmentReason: props.adjustmentReason || defaultAdjustmentReason,
      fieldValue: props.adjustedValue,
      editInProgress: false,
      validationInProgress: false,
      actionNotAllowed: false
    };

    this.handleChangeFieldValue = this.handleChangeFieldValue.bind(this);
    this.handleChangeAdjustmentReason =
      this.handleChangeAdjustmentReason.bind(this);
    this.handleAccept = this.handleAccept.bind(this);

    this.debouncedValidation = debounce(this.validateActionPromise, 1000);
  }

  componentDidMount() {
    this.validateActionPromise();
  }

  handleChangeFieldValue(fieldValue) {
    if (!checkPrecisionLength(fieldValue, PRICE_PRECISION)) {
      this.debouncedValidation();
      this.setState({
        fieldValue: fieldValue,
        validationInProgress: true,
        editInProgress: true
      });
    }
  }

  handleOnBlur(fieldValue) {
    this.setState({
      fieldValue,
      editInProgress: false
    });
  }

  handleChangeAdjustmentReason(adjustmentReason) {
    this.setState({
      adjustmentReason
    });
  }

  handleAccept(event) {
    event.preventDefault();
    this.props.handleAccept(this.state.fieldValue, this.state.adjustmentReason);
  }

  validateActionPromise() {
    const cloneLineItem = cloneDeep(this.props.lineItem);
    cloneLineItem.adjustedPrice[this.props.lineItemField] =
      this.state.fieldValue;

    if (this.props.type === DialogueTypes.Price) {
      cloneLineItem.priceAdjustmentReason = this.state.adjustmentReason;
    } else if (this.props.type === DialogueTypes.Quantity) {
      cloneLineItem.quantityAdjustmentReason = this.state.adjustmentReason;
    }

    api.renewals
      .validateLineItem(cloneLineItem)
      .then((response) => {
        this.setState({
          requiresApproval: checkFieldValidation(
            response.data,
            this.props.lineItemField,
            [APPROVAL_LEVELS.TIER1_APPROVAL, APPROVAL_LEVELS.TIER2_APPROVAL, APPROVAL_LEVELS.TIER3_APPROVAL]
          ),
          actionNotAllowed: checkFieldValidation(
            response.data,
            this.props.lineItemField,
            [VALIDATION_LEVELS.INVALID, VALIDATION_LEVELS.FORBIDDEN]
          ),
          validationInProgress: false
        });
      })
      .catch((error) => {
        //TODO add snack bar?
        this.setState({
          validationInProgress: false
        });
      });
  }

  render() {
    const isPriceDialog = this.props.type === DialogueTypes.Price;
    let discount;
    const startAdornment = !!this.props.startAdornment
      ? this.props.startAdornment
      : '';
    const endAdornment = !!this.props.endAdornment
      ? this.props.endAdornment
      : '';

    if (isPriceDialog) {
      const originalPrice =
        this.props.lineItem.originalPrice[this.props.lineItemField];
      discount = calculateDiscount(originalPrice, this.state.fieldValue);
    }

    return (
      <Modal open={this.props.isModalOpen} onClose={this.props.handleClose}>
        <div className="modal">
          <div style={styles.title}>
            <span
              style={{
                lineHeight: '36px',
                backgroundColor: getRegionalColors().backgroundColor
              }}
            >
              Enter New {this.props.labelText}
            </span>
          </div>
          <form onSubmit={this.handleAccept}>
            <div className="row text-input-row">
              <div className="col-xs-10 col-xs-offset-1">
                <div className="modal-initial-value-label">
                  <Typography>
                    {`${this.props.intl.formatMessage({
                      id: 'current_rate'
                    })}: ${startAdornment}${formatNumber(
                      this.props.initialValue,
                      PRICE_PRECISION
                    )}`}
                  </Typography>
                </div>
              </div>
              <div className="col-xs-10 col-xs-offset-1">
                <FormControl fullWidth>
                  <div>
                    {this.props.intl.formatMessage({
                      id: 'new_rate'
                    })}
                    :
                  </div>
                  <Input
                    value={
                      this.state.editInProgress
                        ? this.state.fieldValue
                        : formatNumber(this.state.fieldValue, PRICE_PRECISION)
                    }
                    onChange={(event) =>
                      this.handleChangeFieldValue(event.target.value)
                    }
                    onBlur={(event) => this.handleOnBlur(event.target.value)}
                    startAdornment={`${startAdornment}`}
                    endAdornment={endAdornment}
                    type="number"
                    inputProps={{
                      step: 0.001
                    }}
                  />
                </FormControl>
              </div>
            </div>
            {this.props.type === DialogueTypes.Price ? (
              <div className="row text-input-row">
                {this.state.validationInProgress ? (
                  <CircularProgress
                    className="col-xs-10 col-xs-offset-1"
                    size={20}
                  />
                ) : (
                  <div
                    className="col-xs-10 col-xs-offset-1"
                    style={
                      this.state.requiresApproval || this.state.actionNotAllowed
                        ? { color: 'red' }
                        : {}
                    }
                  >
                    {this.state.actionNotAllowed
                      ? `${this.props.intl.formatMessage({
                          id: 'action_not_allowed'
                        })} `
                      : null}
                    {`${this.props.intl.formatMessage({
                      id: 'approval_note_1'
                    })} `}
                    {`${formatNumber(
                      Math.abs(discount),
                      DISCOUNT_PRECISION
                    )}% `}
                    {this.state.requiresApproval
                      ? this.props.intl.formatMessage({
                          id: 'approval_note_2'
                        })
                      : discount < 0
                      ? this.props.intl.formatMessage({
                          id: 'increase'
                        })
                      : this.props.intl.formatMessage({
                          id: 'discount'
                        })}
                  </div>
                )}
              </div>
            ) : null}
            {this.state.requiresApproval ? (
              <div className="row">
                <div className="col-xs-10 col-xs-offset-1">
                  <AdjustmentReasonWidget
                    selectedAdjustmentReason={this.state.adjustmentReason}
                    adjustmentReasons={this.props.adjustmentReasons}
                    handleChangeAdjustmentReason={
                      this.handleChangeAdjustmentReason
                    }
                  />
                </div>
              </div>
            ) : null}
            <div className="row buttons">
              <Button
                className="cancel-button"
                raised
                dense
                onClick={this.props.handleClose}
              >
                {this.props.intl.formatMessage({
                  id: 'cancel'
                })}
              </Button>
              <Button
                classes={{
                  root: 'save-button',
                  disabled: 'save-button-disabled'
                }}
                raised
                dense
                type="submit"
                disabled={
                  this.state.validationInProgress ||
                  this.state.actionNotAllowed
                }
              >
                {this.props.intl.formatMessage({
                  id: 'save'
                })}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    );
  }
}

CustomValueModal.propTypes = {
  handleAccept: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  adjustmentReason: PropTypes.object,
  labelText: PropTypes.string.isRequired,
  startAdornment: PropTypes.string,
  adjustmentReasons: PropTypes.array,
  isModalOpen: PropTypes.bool.isRequired,
  initialValue: PropTypes.any.isRequired,
  adjustedValue: PropTypes.any.isRequired,
  lineItem: PropTypes.any.isRequired
};

export default injectIntl(CustomValueModal);
